import { useState, useEffect, useCallback } from 'react';
import { X, Search, Phone, User, MapPin, Calendar, Clock, Bell, Loader2, Plus } from 'lucide-react';
import getApiUrl from '../utils/getApiUrl';

const API = `${getApiUrl()}/api`;

const authFetch = (url, options = {}) => {
  const token = localStorage.getItem("auth_token");
  return fetch(url, {
    ...options,
    credentials: "include",
    headers: { ...(options.headers || {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
};


/**
 * Modal pour ajouter un rappel avec recherche de client/lead
 * Peut être utilisé depuis:
 * - Widget "Mes Rappels" (recherche client nécessaire)
 * - Fiche Client (client pré-sélectionné)
 * - Planning Personnel (date/heure pré-sélectionnée)
 */
export default function AddRappelModal({ 
  isOpen, 
  onClose, 
  onSuccess,
  authUser,
  // Pré-remplissage optionnel
  preSelectedClient = null,
  preSelectedDate = null,
  preSelectedTime = null 
}) {
  // État de recherche
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  
  // Client sélectionné
  const [selectedClient, setSelectedClient] = useState(preSelectedClient);
  
  // Données du rappel
  const [rappelData, setRappelData] = useState({
    date: preSelectedDate || new Date().toISOString().split('T')[0],
    heure: preSelectedTime || '09:00',
    notes: ''
  });
  
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Reset quand le modal s'ouvre
  useEffect(() => {
    if (isOpen) {
      setSelectedClient(preSelectedClient);
      setRappelData({
        date: preSelectedDate || new Date().toISOString().split('T')[0],
        heure: preSelectedTime || '09:00',
        notes: ''
      });
      setSearchQuery('');
      setSearchResults([]);
      setError('');
    }
  }, [isOpen, preSelectedClient, preSelectedDate, preSelectedTime]);

  // Recherche de clients/leads
  const searchClients = useCallback(async (query) => {
    if (!query || query.length < 2) {
      setSearchResults([]);
      return;
    }
    
    setSearching(true);
    try {
      // Rechercher dans les leads entrants
      const resp = await authFetch(`${API}/webhook/leads/search?q=${encodeURIComponent(query)}`);
      if (resp.ok) {
        const data = await resp.json();
        setSearchResults(data.leads || []);
      } else {
        // Fallback: recherche dans les clients pipeline
        const resp2 = await authFetch(`${API}/search`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ nom: query })
        });
        if (resp2.ok) {
          const data2 = await resp2.json();
          // Transformer le format pipeline en format lead
          setSearchResults((data2.clients || []).slice(0, 10).map(c => ({
            lead_id: c.client_id || `row_${c.row_number}`,
            nom: c.data?.['CLIENT NOM'] || c.data?.['NOM'] || '',
            prenom: c.data?.['PRÉNOM'] || c.data?.['PRENOM'] || '',
            telephone: c.data?.['TEL CLIENT'] || c.data?.['TEL'] || '',
            email: c.data?.['EMAIL CLIENT'] || '',
            departement: c.data?.['DÉPARTEMENT'] || c.data?.['DPT'] || '',
            ville: c.data?.['VILLE'] || '',
            code_postal: c.data?.['CODE POSTAL'] || '',
            source: 'pipeline'
          })));
        }
      }
    } catch (e) {
      console.error('Erreur recherche:', e);
    }
    setSearching(false);
  }, []);

  // Debounce de la recherche
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery.length >= 2) {
        searchClients(searchQuery);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, searchClients]);

  // Créer le rappel
  const handleSubmit = async () => {
    if (!selectedClient) {
      setError('Veuillez sélectionner un client');
      return;
    }
    if (!rappelData.date || !rappelData.heure) {
      setError('Veuillez renseigner la date et l\'heure');
      return;
    }

    setSaving(true);
    setError('');

    try {
      const leadId = selectedClient.lead_id || selectedClient.client_id;
      
      const resp = await authFetch(`${API}/webhook/leads/${leadId}/rappel`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          date_rappel: rappelData.date,
          heure_rappel: rappelData.heure,
          telepro_email: authUser?.email || 'admin',
          telepro_nom: authUser?.name || 'Admin',
          notes: rappelData.notes
        })
      });

      if (resp.ok) {
        onSuccess && onSuccess();
        onClose();
      } else {
        const data = await resp.json();
        setError(data.detail || 'Erreur lors de la création du rappel');
      }
    } catch (e) {
      console.error('Erreur:', e);
      setError('Erreur de connexion');
    }

    setSaving(false);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[70] flex items-center justify-center p-4">
      <div className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-4 bg-gradient-to-r from-amber-600 to-orange-600 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
              <Bell className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-white">Nouveau Rappel</h2>
              <p className="text-sm text-white/70">Planifier un appel</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 text-white/70 hover:text-white">
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Sélection du client */}
          {!preSelectedClient && (
            <div className="space-y-3">
              <label className="block text-sm font-medium text-slate-300">
                <User className="w-4 h-4 inline mr-2" />
                Client / Lead
              </label>
              
              {selectedClient ? (
                <div className="bg-slate-800 border border-emerald-500/50 rounded-lg p-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-semibold text-white">
                        {selectedClient.prenom} {selectedClient.nom}
                      </p>
                      <div className="flex items-center gap-2 text-sm text-slate-400">
                        <Phone className="w-3 h-3" />
                        <span>{selectedClient.telephone}</span>
                        {selectedClient.departement && (
                          <>
                            <span className="text-slate-600">•</span>
                            <MapPin className="w-3 h-3" />
                            <span>DPT {selectedClient.departement}</span>
                          </>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={() => setSelectedClient(null)}
                      className="text-slate-400 hover:text-white"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ) : (
                <div className="space-y-2">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Rechercher par nom, prénom ou téléphone..."
                      className="w-full pl-10 pr-4 py-2.5 bg-slate-800 border border-slate-600 rounded-lg text-white placeholder:text-slate-500 focus:outline-none focus:border-amber-500"
                      data-testid="search-client-input"
                    />
                    {searching && (
                      <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-amber-400 animate-spin" />
                    )}
                  </div>
                  
                  {/* Résultats de recherche */}
                  {searchResults.length > 0 && (
                    <div className="bg-slate-800 border border-slate-700 rounded-lg max-h-48 overflow-y-auto">
                      {searchResults.map((client, idx) => (
                        <button
                          key={client.lead_id || idx}
                          onClick={() => {
                            setSelectedClient(client);
                            setSearchQuery('');
                            setSearchResults([]);
                          }}
                          className="w-full p-3 text-left hover:bg-slate-700 border-b border-slate-700 last:border-b-0 transition-colors"
                          data-testid={`search-result-${idx}`}
                        >
                          <p className="font-medium text-white">
                            {client.prenom} {client.nom}
                          </p>
                          <div className="flex items-center gap-2 text-xs text-slate-400">
                            <Phone className="w-3 h-3" />
                            <span>{client.telephone}</span>
                            {client.departement && (
                              <>
                                <span className="text-slate-600">•</span>
                                <span>DPT {client.departement}</span>
                              </>
                            )}
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                  
                  {searchQuery.length >= 2 && !searching && searchResults.length === 0 && (
                    <p className="text-sm text-slate-500 text-center py-2">
                      Aucun résultat pour "{searchQuery}"
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Client pré-sélectionné */}
          {preSelectedClient && (
            <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-3">
              <p className="text-sm text-slate-400 mb-1">Client</p>
              <p className="font-semibold text-white">
                {preSelectedClient.prenom} {preSelectedClient.nom}
              </p>
              {preSelectedClient.telephone && (
                <p className="text-sm text-slate-400">
                  {preSelectedClient.telephone}
                </p>
              )}
            </div>
          )}

          {/* Date et Heure */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1">
                <Calendar className="w-4 h-4 inline mr-2" />
                Date
              </label>
              <input
                type="date"
                value={rappelData.date}
                onChange={(e) => setRappelData({ ...rappelData, date: e.target.value })}
                className="w-full px-3 py-2.5 bg-slate-800 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-amber-500"
                data-testid="rappel-date-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1">
                <Clock className="w-4 h-4 inline mr-2" />
                Heure
              </label>
              <input
                type="time"
                value={rappelData.heure}
                onChange={(e) => setRappelData({ ...rappelData, heure: e.target.value })}
                className="w-full px-3 py-2.5 bg-slate-800 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-amber-500"
                data-testid="rappel-heure-input"
              />
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1">
              Notes (optionnel)
            </label>
            <textarea
              value={rappelData.notes}
              onChange={(e) => setRappelData({ ...rappelData, notes: e.target.value })}
              placeholder="Raison du rappel, informations utiles..."
              rows={3}
              className="w-full px-3 py-2.5 bg-slate-800 border border-slate-600 rounded-lg text-white placeholder:text-slate-500 focus:outline-none focus:border-amber-500 resize-none"
              data-testid="rappel-notes-input"
            />
          </div>

          {/* Erreur */}
          {error && (
            <div className="bg-red-500/20 border border-red-500/50 rounded-lg p-3 text-red-400 text-sm">
              {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-slate-700 flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2.5 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
          >
            Annuler
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving || (!selectedClient && !preSelectedClient)}
            className="flex-1 px-4 py-2.5 bg-amber-600 hover:bg-amber-500 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg font-medium flex items-center justify-center gap-2 transition-colors"
            data-testid="confirm-add-rappel-btn"
          >
            {saving ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Plus className="w-4 h-4" />
            )}
            Créer le rappel
          </button>
        </div>
      </div>
    </div>
  );
}
